import type { CustomRoleTaxonomySelection } from '@revelio/data-access';
import {
  GENERATED_DEFAULT_CUSTOM_ROLE_DEFAULT,
  GENERATED_ROLE_DEFAULTS_VERSION,
} from '@revelio/data-access/role-defaults';

import { UserPreferenceKey } from '../../../../shared/user-preferences/types';
import { PreferenceBrowserStorage } from '../../../../shared/user-preferences/utils/preference-session-storage';
import { getPreferenceScope } from '../../../../shared/user-preferences/utils/preferences';
import { reconcileVersionedDefaults } from '../../../../shared/user-preferences/utils/reconcileVersionedDefaults';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import { ALL_FILTER_DEFINITIONS } from '../engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';
import type { TaxonomyConfig } from '../types/taxonomy';
import { getRoleDefaultSelectionsForTaxonomy } from '../utils/getRoleDefaultSelectionsForTaxonomy';

export const DEFAULT_SKILL_TAXONOMY_ID = 0;

export const DEFAULT_FILTERS: Filter[] = Object.values(
  ALL_FILTER_DEFINITIONS
).sort((a, b) => a.name.localeCompare(b.name));

export const DEFAULT_PRIMARY_COMPANIES: TreeFilter['selections'] = [
  { id: '8_256_1116644', name: 'Workday', levelId: 'company' },
  { id: '8_257_919809', name: 'Snowflake', levelId: 'company' },
  { id: '8_257_1005273', name: 'MongoDB', levelId: 'company' },
  { id: '8_248_1010813', name: 'Palantir', levelId: 'company' },
  { id: '8_256_256277', name: 'Slack', levelId: 'company' },
];

export const DEFAULT_PRIMARY_GEOGRAPHY: TreeFilter['selections'] = [
  { id: '1_1_2', name: 'New York City', levelId: 'msa' },
  { id: '5_4_1', name: 'London', levelId: 'msa' },
  { id: '3_8_3', name: 'Paris', levelId: 'msa' },
  { id: '1_1_21', name: 'San Francisco', levelId: 'msa' },
  { id: '4_5_5', name: 'Sao Paulo', levelId: 'msa' },
];

export const DEFAULT_PRIMARY_CUSTOM_ROLE: TreeFilter['selections'] =
  GENERATED_DEFAULT_CUSTOM_ROLE_DEFAULT;

export type DefaultSelectionOverride = {
  [FilterId.COMPANY]?: TreeFilter['selections'];
  [FilterId.GEOGRAPHY]?: TreeFilter['selections'];
  [FilterId.ROLE]?: TreeFilter['selections'];
};

export const createDefaultFilterMap = (
  primaryFilterIdToSet?: PrimaryFilterId,
  taxonomyConfig?: TaxonomyConfig,
  defaultSelections?: DefaultSelectionOverride
): Map<FilterId, Filter> => {
  const filterMap = new Map<FilterId, Filter>();

  Object.values(ALL_FILTER_DEFINITIONS).forEach((definition) => {
    // Skip MULTI filters - only include single versions by default
    // MULTI filters can still be added explicitly via templates
    if (definition.type === FilterType.MULTI) {
      return;
    }

    const filterInstance: Filter = { ...definition };
    if (definition.id === FilterId.ROLE_TAXONOMY) {
      (filterInstance as SingleValueFilter<CustomRoleTaxonomySelection>).value =
        {
          default: true,
          version: taxonomyConfig?.roleTaxonomyVersion,
        };
    }
    if (definition.id === FilterId.SKILL_TAXONOMY) {
      (
        filterInstance as SingleValueFilter<CustomSkillTaxonomySelection>
      ).value = {
        taxonomyId: DEFAULT_SKILL_TAXONOMY_ID,
        version: taxonomyConfig?.skillTaxonomyVersion,
      };
    }

    filterMap.set(definition.id, filterInstance);
  });

  if (!primaryFilterIdToSet) return filterMap;

  const defaultSelectionsForPrimary = (() => {
    // Try to get user preferences from the cache for this specific filter's scope
    const scopeForFilter = getPreferenceScope({
      primaryFilterId: primaryFilterIdToSet,
    });
    const preferencesForScope = PreferenceBrowserStorage.get(scopeForFilter);
    const userDefaultSelections =
      preferencesForScope?.[UserPreferenceKey.DefaultFilters];
    const hasSavedSelections =
      !!userDefaultSelections && userDefaultSelections.length > 0;

    if (primaryFilterIdToSet === FilterId.ROLE) {
      // Version-gate the saved role defaults: a taxonomy bump (saved stamp !=
      // live version) must not serve stale selections. When not reusable we fall
      // through to the taxonomy/override/hard-coded chain below — the override is
      // where FilterProvider injects the first-from-live fallback.
      const { source } = reconcileVersionedDefaults({
        hasSaved: hasSavedSelections,
        savedVersion:
          preferencesForScope?.[UserPreferenceKey.Taxonomies]
            ?.roleTaxonomyVersion,
        currentVersion: taxonomyConfig?.roleTaxonomyVersion,
        bakedVersion: GENERATED_ROLE_DEFAULTS_VERSION,
      });
      if (source === 'saved' && userDefaultSelections) {
        return userDefaultSelections;
      }
    } else if (hasSavedSelections) {
      // COMPANY / GEOGRAPHY have no taxonomy version — saved defaults win as-is.
      return userDefaultSelections;
    }

    // Fallback to hard-coded defaults
    switch (primaryFilterIdToSet) {
      case FilterId.COMPANY:
        return (
          defaultSelections?.[FilterId.COMPANY] ?? DEFAULT_PRIMARY_COMPANIES
        );
      case FilterId.GEOGRAPHY:
        return (
          defaultSelections?.[FilterId.GEOGRAPHY] ?? DEFAULT_PRIMARY_GEOGRAPHY
        );
      case FilterId.ROLE: {
        const rolePref =
          preferencesForScope?.[UserPreferenceKey.Taxonomies]?.role;
        const roleTaxonomySelection: CustomRoleTaxonomySelection =
          rolePref && rolePref !== 'adaptive' && typeof rolePref === 'object'
            ? rolePref
            : { default: true };

        const taxonomyDefaults = getRoleDefaultSelectionsForTaxonomy(
          roleTaxonomySelection
        );

        return (
          defaultSelections?.[FilterId.ROLE] ??
          taxonomyDefaults ??
          DEFAULT_PRIMARY_CUSTOM_ROLE
        );
      }
      default: {
        return [];
      }
    }
  })();

  const primaryFilterCurrentInstance = filterMap.get(primaryFilterIdToSet);

  if (primaryFilterCurrentInstance) {
    const mutablePrimaryFilter = { ...primaryFilterCurrentInstance };

    if (mutablePrimaryFilter.type === FilterType.TREE) {
      const primaryTreeFilter = mutablePrimaryFilter;
      primaryTreeFilter.selections = defaultSelectionsForPrimary;
      primaryTreeFilter.isPrimary = true;
      primaryTreeFilter.primarySelections = defaultSelectionsForPrimary.map(
        (selection) => ({
          ...selection,
          isActive: true,
        })
      );
      filterMap.set(primaryFilterIdToSet, primaryTreeFilter);
    } else {
      mutablePrimaryFilter.isPrimary = true;
      filterMap.set(primaryFilterIdToSet, mutablePrimaryFilter);
    }
  }

  return filterMap;
};
